import {
  CopyOutlined,
  DownloadOutlined,
  InfoCircleOutlined,
} from '@ant-design/icons';
import { memo, useMemo } from 'react';
import uuid from 'react-uuid';

import { Flex } from '@/components/base';
import Button from '@/components/Button/Button';
import TooltipV2 from '@/components/Tooltip/TooltipV2';
import Typo from '@/components/Typography/Typo';
import useWordCloudPayload from '@/features/InsightExplorer/hooks/useWordCloudPayload';
import { TranslateLanguageSelect } from '@/features/WordCloud/components/TranslateLanguageSelect';
import { WordCloud } from '@/features/WordCloud/components/WordCloud';
import { useWordCloudAction } from '@/features/WordCloud/hooks/useWordCloudAction';
import useWordCloudFeedbackListModal from '@/features/WordCloud/hooks/useWordCloudFeedbackListModal';
import { useWordCloudTranslation } from '@/features/WordCloud/hooks/useWordCloudTranslation';
import type { WordCloudItem } from '@/features/WordCloud/types';
import useColor from '@/hooks/domain/useColor';

import CardContainer from '../CardContainer';

interface WordCloudSectionProps {
  words?: WordCloudItem[];
  isLoading?: boolean;
}

const WordCloudSection = memo(({ words, isLoading }: WordCloudSectionProps) => {
  const colors = useColor();
  const openWordCloudFeedbackListModal = useWordCloudFeedbackListModal();
  const wordCloudPayload = useWordCloudPayload();

  const { handleCopyToClipboard, handleDownloadPNG } = useWordCloudAction();

  const {
    isSupported: isTranslationSupported,
    targetLanguage,
    setTargetLanguage,
    translatedWords,
    isTranslating,
  } = useWordCloudTranslation({
    words: words || [],
    enabled: !isLoading,
  });

  const wordCloudId = useMemo(() => uuid(), []);

  const hasWords = words && words.length > 0;

  return (
    <CardContainer g={24} padding="20px" height="480px">
      <Flex j="space-between" a="center">
        <Flex a="center" g={6}>
          <Typo type="label1" weight="sb" color={colors.gray900}>
            Word cloud
          </Typo>
          <TooltipV2
            tooltipNode="Wordcloud is generated from the same aggregated dataset used by the chart query."
            style={{
              width: 204,
            }}
          >
            <InfoCircleOutlined
              style={{ fontSize: 16, color: colors.gray500 }}
            />
          </TooltipV2>
        </Flex>
        <Flex g={6}>
          {isTranslationSupported && hasWords && (
            <TranslateLanguageSelect
              value={targetLanguage}
              onChange={setTargetLanguage}
              isLoading={isTranslating}
              disabled={isLoading}
            />
          )}
          <Button
            leftIcon={<DownloadOutlined />}
            variant="ghost"
            sizeType="m"
            onClick={() => handleDownloadPNG(wordCloudId)}
          />
          <Button
            leftIcon={<CopyOutlined />}
            variant="ghost"
            sizeType="m"
            onClick={() => handleCopyToClipboard(wordCloudId)}
          />
        </Flex>
      </Flex>

      <WordCloud
        wordCloudId={wordCloudId}
        isLoading={isLoading || isTranslating}
        words={translatedWords}
        onWordClick={(word: { text: string; originalKeyword?: string }) => {
          // Use original Korean keyword for detail lookup when available
          const keyword = word.originalKeyword || word.text;
          openWordCloudFeedbackListModal({
            keyword,
            translatedKeyword: word.text,
            filter: wordCloudPayload,
          });
        }}
      />
    </CardContainer>
  );
});

WordCloudSection.displayName = 'WordCloudSection';

export default WordCloudSection;
